import { BetslipBoost } from "@/types/Betslip";
import { SportEvent, Selection } from "@/types/SportsApiResponses";
import type { PlacementError } from "@/utils/placementNotice";

type BetslipSelectionAction = "add" | "delete";

enum BetslipCategory {
  Single = "single",
  Combination = "combination",
  System = "system",
}

interface BetslipRules {
  allowSameEventSelections: boolean;
  mixLiveAndLineEnabled: boolean;
  basketballMixLiveAndLineEnabled: boolean;
  mixFootballAndBasketballEnabled: boolean;
  mixVirtualFootballAndOthersEnabled: boolean;
  mixVirtualBasketballAndOthersEnabled: boolean;
  maxTotalOdds: number;
  combinationInsuranceEnabled: boolean;
  combinationBoostEnabled: boolean;
  systemBetsEnabled: boolean;
  minStakeAmount: number;
  maxStakeAmount: number;
  maxPayoutAmount: number;
  lineMinOdds: number;
  lineMaxOdds: number;
  liveMinOdds: number;
  liveMaxOdds: number;
  liveBettingDelaySeconds: number;
  oddsChangeAllowed: boolean;
  couponCancelWindowMinutes: number;
}

interface AddSelectionPayload {
  event: SportEvent;
  selection: Selection;
}

interface UpdateSelectionPayload {
  selection: Selection;
  event: SportEvent;
}

interface SetStakePayload {
  key: string | number;
  category: string;
  value: string;
}

interface DeleteStakePayload {
  key: string | number;
  category: string;
}

interface BetslipCategorySettings {
  minSelectionsCount: number;
  maxSelectionsCount: number;
}

interface BetslipCategoryStakes {
  [key: string]: string;
}

interface SelectionsMap {
  [key: string]: Selection;
}

type BetslipSettingsMap = Record<BetslipCategory, BetslipCategorySettings>;
type BetslipBoostMap = Record<BetslipCategory, BetslipBoost | undefined>;
type BetslipStakesMap = Record<BetslipCategory, BetslipCategoryStakes>;

interface BetslipState {
  lastActivityTime: number | null;
  // Id of the user this persisted slip belongs to. Guards against one user's
  // slip surviving in localStorage and showing up for the next user on a
  // shared device (see useBetslipOwnerGuard / decideBetslipOwnership). Null
  // means "unclaimed" (fresh, or a legacy slip from before this field).
  ownerUserId: number | null;
  isLocked: boolean;

  rules: BetslipRules;

  systemComboStake: string;

  stakesMap: BetslipStakesMap;
  boostMap: BetslipBoostMap;
  settingsMap: BetslipSettingsMap;

  category: BetslipCategory;

  eventsMap: Record<number, SportEvent>;
  events: number[];

  originalOddsMap: Record<string, number>;
  selectionMap: SelectionsMap;
  selections: string[];

  selectionsByEvent: Record<number, string[]>;
  selectionsByMarket: Record<string, string[]>;

  name: string;
  error: string | null;
  // The server's reason for refusing the last placement (translated sentence
  // plus the typed tranKey/params it came with, e.g. a match risk limit and
  // how much still fits). Distinct from `error`, which is the client-side
  // amount validation. Set by the placement mutation, cleared by the next
  // attempt and by any change to the slip; never persisted.
  placementError: PlacementError | null;
}

interface BetslipActions {
  setIsLocked(isLocked: boolean): void;
  setCategory(category: BetslipCategory): void;

  addSelection(selection: AddSelectionPayload): void;
  updateSelection(payload: UpdateSelectionPayload): void;

  deleteSelection(id: string): void;
  // Atomic batch removal — drop a whole closed VR round in one store update.
  deleteSelections(ids: string[]): void;
  deleteEventSelections(eventId: number): void;

  setBoost(categoty: BetslipCategory, boost: BetslipBoost): void;
  deleteBoost(categoty: BetslipCategory): void;

  setStake(payload: SetStakePayload): void;
  setSystemComboStake(value: string, comboKeys: number[]): void;
  deleteStake(payload: DeleteStakePayload): void;
  clearStakes(category: string): void;
  setName(name: string): void;
  setRules(rules: BetslipRules): void;
  setSettingsMap(settingsMap: BetslipSettingsMap): void;
  setError(error: string | null): void;
  setPlacementError(error: PlacementError | null): void;
  clear(): void;
}

type BetslipStore = BetslipState & BetslipActions;

export { BetslipCategory as BetslipCategory };
export type {
  BetslipRules,
  BetslipStore,
  BetslipState,
  SelectionsMap,
  BetslipBoostMap,
  BetslipSettingsMap,
  BetslipStakesMap,
  BetslipCategoryStakes,
  BetslipSelectionAction,
  AddSelectionPayload,
};
